import React from 'react';
import { Modal, Button, ButtonWithSpinner } from '@makemydeal/dr-ui-bricks-components';
import {
    TradeInModalLayerStyles,
    TradeInModalContainer,
    TradeInListItem,
    TradeInCheckboxWrapper,
    TradeInVehicleInfo,
    TradeInStatusBadge,
    TradeInActions,
    CustomCheckbox
} from './TradeInModal.style';
import { formatVehicleName, isAppraisedTrade } from '../../utils/utils';
import { useTradeInModal } from '../../hooks';
import type { TradeInModalProps } from '../../types';
import { useCustomerVehiclesSelector, useCurrentDealTradeIns } from '../../selectors';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';

export const TradeInModal: React.FC<TradeInModalProps> = ({ tradeIns, currentDeal, isVisibleTradeInModal }) => {
    const {
        isSubmitDisabled,
        handleCheckboxChange,
        isSelected,
        isInCurrentDeal,
        handleCancel,
        handleContinue,
        hasCurrentDealAllTradeInsSelected
    } = useTradeInModal(currentDeal, tradeIns);
    const { updateTradeInsStatus } = useCustomerVehiclesSelector();

    // Access deduplicated trade-ins with full financial data (acv, value etc.) across all deals
    const dealsTradeIns = useCurrentDealTradeIns();

    if (!isVisibleTradeInModal || hasCurrentDealAllTradeInsSelected()) return null;

    return (
        <>
            <TradeInModalLayerStyles />
            <Modal title="Add Trade-In Information?" description="" className="trade-in-modal-layer">
                <TradeInModalContainer data-testid="trade-in-modal" className="trade-in-modal-content">
                    {tradeIns.map((tradeIn) => {
                        const vehicleName = formatVehicleName(tradeIn);
                        const isVinSelected = isSelected(tradeIn.vin || '') || isInCurrentDeal(tradeIn.vin || '');

                        return (
                            <TradeInListItem key={tradeIn.vin} isSelected={isVinSelected}>
                                <TradeInCheckboxWrapper>
                                    <CustomCheckbox
                                        checked={isVinSelected}
                                        onChange={handleCheckboxChange(tradeIn.vin || '')}
                                        data-testid={`trade-in-checkbox-${tradeIn.vin}`}
                                        disabled={isInCurrentDeal(tradeIn.vin || '')}
                                    />
                                    <TradeInVehicleInfo isSelected={isVinSelected} data-testid={`trade-in-vehicle-${tradeIn.vin}`}>
                                        {vehicleName}
                                    </TradeInVehicleInfo>
                                </TradeInCheckboxWrapper>
                                {isAppraisedTrade(tradeIn, dealsTradeIns, isInCurrentDeal) && (
                                    <TradeInStatusBadge data-testid={`trade-in-status-${tradeIn.vin}`}>
                                        Appraised
                                    </TradeInStatusBadge>
                                )}
                            </TradeInListItem>
                        );
                    })}

                    <TradeInActions>
                        <ButtonWithSpinner
                            buttonStyle="primary"
                            onClick={handleContinue}
                            data-testid="trade-in-modal-continue-button"
                            className="continue-button"
                            disabled={isSubmitDisabled || updateTradeInsStatus === ApiRequestStatus.Loading}
                            isLoading={updateTradeInsStatus === ApiRequestStatus.Loading}
                        >
                            Continue to Apply to Deal
                        </ButtonWithSpinner>
                        <Button
                            buttonStyle="secondary"
                            onClick={handleCancel}
                            data-testid="trade-in-modal-cancel-button"
                            className="cancel-button"
                        >
                            Cancel
                        </Button>
                    </TradeInActions>
                </TradeInModalContainer>
            </Modal>
        </>
    );
};

export default TradeInModal;
